import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { deleteDraftFiles, loadPostingDraft, newDraftCacheId, postingDraftKey, removeDraftMetadata, saveDraftFiles, saveDraftMetadata, type DraftFiles } from "./postingDraftStorage";

type Options<T> = { actor: string; type: string; context?: string | null; enabled?: boolean; template: T; snapshot: T; files: DraftFiles; upgrade?: (value: unknown) => unknown; valid: (value: T) => boolean; restore: (data: T, files: DraftFiles, missingFiles: boolean) => void };

/** Callers are keyed by actor/context/type and own field validation and submission. */
export function usePostingDraft<T>(options: Options<T>) {
  const key = postingDraftKey(options.actor, options.type, options.context);
  const initial = useRef(options);
  const latest = useRef(options);
  latest.current = options;
  const cacheId = useRef<string>(newDraftCacheId());
  const stopped = useRef(false);
  const [ready, setReady] = useState(options.enabled === false);
  const [message, setMessage] = useState("");
  const [warning, setWarning] = useState("");
  const previousFiles = useRef<DraftFiles | null>(null);
  const fileQueue = useRef(Promise.resolve());
  const fileGeneration = useRef(0);

  useEffect(() => {
    if (initial.current.enabled === false) return;
    let active = true;
    const { template, valid, restore, upgrade } = initial.current;
    void loadPostingDraft(key, template, valid, upgrade).then(saved => {
      if (!active || stopped.current) return;
      if (saved) {
        cacheId.current = saved.cacheId;
        restore(saved.data, saved.files, saved.missingFiles);
        if (saved.missingFiles) setWarning("Some attachments could not be restored. Please add them again before publishing.");
      }
    }).catch(() => {
      if (active) setWarning("Your saved draft could not be restored. Please check this form before continuing.");
    }).finally(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, [key]);

  const checkpoint = (snapshot: T = latest.current.snapshot) => {
    if (stopped.current || latest.current.enabled === false) return;
    try { saveDraftMetadata(key, cacheId.current, snapshot, latest.current.files); }
    catch { setWarning("Draft recovery is unavailable. Keep this tab open until you finish posting."); }
  };

  useLayoutEffect(() => {
    if (!ready || stopped.current || options.enabled === false) return;
    checkpoint();
    const groups: (keyof DraftFiles)[] = ["images", "previousImages", "documents", "logo"];
    const changed = !previousFiles.current || groups.some(group => previousFiles.current?.[group] !== options.files[group]);
    previousFiles.current = options.files;
    if (!changed) return;
    const files = options.files;
    const id = cacheId.current;
    const hasFiles = Object.values(files).some(group => group.length);
    setMessage(hasFiles ? "Saving draft…" : "Draft saved in this tab.");
    const generation = ++fileGeneration.current;
    if (!hasFiles) return;
    fileQueue.current = fileQueue.current.catch(() => {}).then(async () => {
      if (stopped.current) return;
      await saveDraftFiles(id, files);
      if (!stopped.current && generation === fileGeneration.current) setMessage("Draft saved in this tab.");
    }).catch(() => { if (!stopped.current && generation === fileGeneration.current) setWarning("Attachments could not be saved for refresh. Keep this tab open or add them again after refreshing."); });
  });

  useEffect(() => {
    if (!ready || options.enabled === false) return;
    const flush = () => checkpoint();
    const cleared = (event: Event) => {
      if (event instanceof CustomEvent && event.detail === initial.current.actor) {
        stopped.current = true;
        void fileQueue.current.finally(() => deleteDraftFiles(cacheId.current)).catch(() => {});
      }
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("posting-drafts-cleared", cleared);
    return () => { window.removeEventListener("pagehide", flush); window.removeEventListener("posting-drafts-cleared", cleared); };
  }, [ready, key, options.enabled]);

  const clear = () => {
    if (latest.current.enabled === false) return;
    stopped.current = true;
    try { removeDraftMetadata(key); } catch { setWarning("Saved draft could not be cleared."); }
    void fileQueue.current.finally(() => deleteDraftFiles(cacheId.current)).catch(() => {});
  };
  return { ready, message, warning, clear, checkpoint };
}
